import {Card, Text} from '@sanity/ui'
import {useEffect, useMemo, useRef} from 'react'

import {useCancelUpload} from '../hooks/useCancelUpload'
import type {MuxInputProps, PluginConfig, VideoAssetDocument} from '../util/types'
import {TopControls} from './Player.styled'
import {UploadProgress} from './UploadProgress'
import VideoPlayer from './VideoPlayer'

interface Props extends Pick<MuxInputProps, 'onChange' | 'readOnly'> {
  buttons?: React.ReactNode
  asset: VideoAssetDocument
  config?: PluginConfig
}

const Player = ({asset, buttons, readOnly, onChange, config}: Props) => {
  const isLoading = useMemo<boolean | string>(() => {
    if (asset?.status === 'preparing') {
      return 'Preparing the video'
    }
    if (asset?.status === 'waiting_for_upload') {
      return 'Waiting for upload to start'
    }
    if (asset?.status === 'waiting') {
      return 'Processing upload'
    }
    if (asset?.status === 'ready') {
      return false
    }
    if (typeof asset?.status === 'undefined') {
      return false
    }

    return true
  }, [asset])
  const isPreparingStaticRenditions = useMemo<boolean>(() => {
    // Legacy: If static_renditions has a status field, it was created with mp4_support (deprecated)
    // We don't process this old format, just return false
    // Note: 'disabled' status is valid in the new format when no renditions were requested
    if (
      asset?.data?.static_renditions?.status &&
      asset?.data?.static_renditions?.status !== 'disabled'
    ) {
      return false
    }

    // Check if any file in static_renditions is still preparing
    const files = asset?.data?.static_renditions?.files
    if (!files || files.length === 0) {
      return false
    }
    return files.some((file) => file.status === 'preparing')
    // oxlint-disable-next-line react/preserve-manual-memoization
  }, [asset?.data?.static_renditions?.status, asset?.data?.static_renditions?.files])
  const playRef = useRef<HTMLDivElement>(null)
  const muteRef = useRef<HTMLDivElement>(null)
  const handleCancelUpload = useCancelUpload(asset, onChange)

  useEffect(() => {
    const style = document.createElement('style')
    style.innerHTML = 'button svg { vertical-align: middle; }'

    if (playRef.current?.shadowRoot) {
      playRef.current.shadowRoot.appendChild(style)
    }
    if (muteRef?.current?.shadowRoot) {
      muteRef.current.shadowRoot.appendChild(style.cloneNode(true))
    }
  }, [])

  useEffect(() => {
    if (asset?.status === 'errored') {
      handleCancelUpload()
      // @TODO use better error handling
      throw new Error(asset.data?.errors?.messages?.join(' '))
    }
  }, [asset.data?.errors?.messages, asset?.status, handleCancelUpload])

  if (!asset || !asset.status) {
    return null
  }

  if (isLoading) {
    return (
      <UploadProgress
        progress={100}
        filename={asset?.filename}
        text={(isLoading !== true && isLoading) || 'Waiting for Mux to complete the upload'}
        onCancel={readOnly ? undefined : () => handleCancelUpload()}
      />
    )
  }

  return (
    <VideoPlayer asset={asset} hlsConfig={config?.hlsConfig}>
      {buttons && <TopControls slot="top-chrome">{buttons}</TopControls>}
      {isPreparingStaticRenditions && (
        <Card
          padding={2}
          radius={1}
          style={{
            background: 'var(--card-fg-color)',
            position: 'absolute',
            top: '0.5em',
            left: '0.5em',
          }}
        >
          <Text size={1} style={{color: 'var(--card-bg-color)'}}>
            MUX is preparing static renditions, please stand by
          </Text>
        </Card>
      )}
    </VideoPlayer>
  )
}

export default Player
